import styled from "styled-components";
import React, { useState } from "react";
import emailjs from "emailjs-com";

const Text = styled.div<{
  marginTop?: string;
  color?: string;
  fontWeight?: string | number;
  fontSize?: string;
  textAlign?: string;

  mobileMarginTop?: string; // 모바일 전용 margin-top
  mobileFontSize?: string; // 모바일 전용 font-size
}>`
  display: flex;
  margin-top: ${({ marginTop = "0" }) => marginTop};
  color: ${({ color = "white" }) => color};
  font-weight: ${({ fontWeight = "bold" }) => fontWeight};
  font-size: ${({ fontSize = "32px" }) => fontSize};
  font-family: "Pretendard";
  text-align: ${({ textAlign = "center" }) => textAlign};
  white-space: pre-wrap;
  word-break: break-word;

  @media (max-width: 1550px) {
    font-size: ${({ mobileFontSize }) => mobileFontSize || "inherit"};
    margin-top: ${({ mobileMarginTop = "30px" }) => mobileMarginTop};
  }
`;

const CustomContainer = styled.div<{
  padding?: string;
  marginTop?: string;
  width?: string;
  height?: string;
  backgroundColor?: string;
  borderRadius?: string;
  display?: string;
  alignItems?: string;
  justifyContent?: string;
  flexDirection?: string;
  mobileFlexDirection?: string;
  gap?: string;
  border?: string;

  // 반응형 옵션
  mobilePadding?: string;
  mobileMargin?: string;
  mobileWidth?: string;
  mobileHeight?: string;
  mobileBackgroundColor?: string;
  mobileBorderRadius?: string;
  mobileGap?: string;
}>`
  display: ${({ display = "flex" }) => display};
  padding: ${({ padding = "0px 0px 0px 0px" }) => padding};
  margin-top: ${({ marginTop = "0" }) => marginTop};
  width: ${({ width = "100%" }) => width};
  height: ${({ height = "auto" }) => height};
  background-color: ${({ backgroundColor = "transparent" }) => backgroundColor};
  border-radius: ${({ borderRadius = "0" }) => borderRadius};
  border: ${({ border = "none" }) => border};
  align-items: ${({ alignItems = "center" }) => alignItems};
  justify-content: ${({ justifyContent = "center" }) => justifyContent};
  flex-direction: ${({ flexDirection = "row" }) => flexDirection};
  gap: ${({ gap = "0" }) => gap};
  box-sizing: border-box;

  @media (max-width: 1550px) {
    padding: ${({ mobilePadding }) => mobilePadding || "inherit"};
    margin: ${({ mobileMargin }) => mobileMargin || "inherit"};
    width: ${({ mobileWidth }) => mobileWidth || "inherit"};
    height: ${({ mobileHeight }) => mobileHeight || "inherit"};
    background-color: ${({ mobileBackgroundColor }) =>
      mobileBackgroundColor || "inherit"};
    border-radius: ${({ mobileBorderRadius }) =>
      mobileBorderRadius || "inherit"};
    flex-direction: ${({ mobileFlexDirection }) =>
      mobileFlexDirection || "inherit"};
    gap: ${({ mobileGap }) => mobileGap || "inherit"};
  }
`;

const Input = styled.input`
  display: flex;
  flex: 1;
  padding: 12px;
  font-size: 16px;
  box-sizing: border-box;
  border-radius: 4px;
  border: 1px solid #2051ad;
  background-color: #ffffff;
  outline: none;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  height: 100%;
  transition: background-color 0.2s ease-in-out, border 0.2s ease-in-out;
  &:hover {
    background-color: #c5d9ff;
  }
  &:focus {
    border: 1px solid #103d80; /* 클릭 시 더 진한 파란색 */
    background-color: #d9e6ff; /* 클릭 후 입력 중일 때 색 */
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  height: 120px;
  padding: 12px;
  font-size: 16px;
  box-sizing: border-box;
  border-radius: 5px;
  border: 1px solid #ccc;
  background-color: #eef;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: bold;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const MobileRadioGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr); // 2열로 정렬
  gap: 10px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  color: #fff;
  @media (max-width: 1550px) {
    font-size: 14px; // 모바일에서는 폰트 크기 줄이기
  }
`;

const Button = styled.button`
  margin-top: 20px;
  display: inline-block; /* 또는 block */
  width: 270px;
  height: 65px;
  padding: 15px;
  font-size: 18px;
  border-radius: 100px;
  border: none;
  background-color: white;
  color: black;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.3s ease-in-out, transform 0.2s ease-in-out;

  &:hover {
    background-color: #2051ad;
    color: white;
    transform: scale(1.05);
  }
`;

const validateEmail = (email: string) => {
  return /\S+@\S+\.\S+/.test(email);
};

const validatePhoneNumber = (number) => {
  const phoneRegex = /^010-\d{4}-\d{4}$/;
  return phoneRegex.test(number);
};

const MobileHomepageEnquiry = () => {
  const [formData, setFormData] = useState({
    name: "",
    hospitalName: "",
    phoneNumber: "",
    email: "",
    homepage: "",
    homepageAddress: "",
    budget: "",
    request: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const serviceId = "slime";
    const publicKey = "J-omw33g2kOoio9_Q";
    const templateId = "template_gro9if9";

    const requiredFields = ["name", "hospitalName", "phoneNumber"];
    for (const field of requiredFields) {
      if (!formData[field as keyof typeof formData]) {
        alert("모든 필수 입력 항목을 채워주세요.");
        return;
      }
    }

    // EmailJS에 보낼 데이터 객체
    const data = `
      이름: ${formData.name}
      병원명: ${formData.hospitalName}
      연락처: ${formData.phoneNumber}
      이메일: ${formData.email}
      홈페이지 유무: ${formData.homepage}
      홈페이지 주소: ${formData.homepageAddress || "없음"}
      예상비용: ${formData.budget}
      요청사항: ${formData.request || "없음"}
    `;

    // EmailJS를 통한 이메일 전송
    emailjs
      .send(serviceId, templateId, { message: data }, publicKey)
      .then(() => {
        alert("문의가 성공적으로 접수되었습니다.");
      })
      .catch((error) => {
        alert("문의 접수에 실패하였습니다. 다시 시도해주세요.");
      });

    console.log("폼 제출됨:", formData);
  };

  return (
    <form id="mobileEnquireForm" onSubmit={handleSubmit}>
      <CustomContainer flexDirection="column" gap="20px" alignItems="center">
        <CustomContainer
          mobileFlexDirection="column"
          height="100%"
          mobileWidth="340px"
          gap="10px"
          marginTop="10px"
        >
          <CustomContainer
            flexDirection="column"
            gap="10px"
            alignItems="flex-start"
            width="340px"
          >
            <Text fontSize="20px" fontColor="white" mobileFontSize="18px">
              이름
            </Text>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
          </CustomContainer>
          <CustomContainer
            flexDirection="column"
            gap="10px"
            alignItems="flex-start"
            width="340px"
          >
            <Text fontSize="20px" fontColor="white">
              병원명
            </Text>
            <Input
              type="text"
              name="hospitalName"
              value={formData.hospitalName}
              onChange={handleChange}
            />
          </CustomContainer>

          <CustomContainer
            flexDirection="row"
            width="700px"
            height="90px"
            gap="20px"
          >
            <CustomContainer
              flexDirection="column"
              gap="10px"
              alignItems="flex-start"
              width="340px"
            >
              <Text fontSize="20px" fontColor="white">
                담당자 번호
              </Text>
              <Input
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
              />
            </CustomContainer>
            <CustomContainer
              flexDirection="column"
              gap="10px"
              alignItems="flex-start"
              width="340px"
            >
              <Text fontSize="20px" fontColor="white">
                이메일
              </Text>
              <Input
                type="text"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </CustomContainer>
          </CustomContainer>
          <CustomContainer
            flexDirection="row"
            width="700px"
            height="90px"
            gap="20px"
          >
            <CustomContainer
              flexDirection="column"
              gap="10px"
              alignItems="flex-start"
              width="340px"
            >
              <Text fontSize="20px" fontColor="white">
                홈페이지 유무
              </Text>

              <RadioGroup>
                <RadioLabel>
                  <input
                    type="radio"
                    name="homepage"
                    value="none"
                    onChange={handleChange}
                    check={formData.homepage === "none"}
                  />
                  없음 (신규)
                </RadioLabel>
                <RadioLabel>
                  <input
                    type="radio"
                    name="homepage"
                    value="existing"
                    onChange={handleChange}
                    check={formData.homepage === "existing"}
                  />
                  있음 (리뉴얼 예정)
                </RadioLabel>
              </RadioGroup>
            </CustomContainer>
            <CustomContainer
              flexDirection="column"
              gap="10px"
              alignItems="flex-start"
              width="340px"
            >
              <Text fontSize="20px" fontColor="white">
                홈페이지 주소
              </Text>
              <Input
                type="text"
                name="homepageAddress"
                value={formData.homepageAddress}
                onChange={handleChange}
              />
            </CustomContainer>
          </CustomContainer>
          <CustomContainer
            flexDirection="row"
            width="700px"
            height="90px"
            gap="20px"
          >
            <CustomContainer
              flexDirection="column"
              gap="10px"
              alignItems="flex-start"
              justifyContent="flex-start"
              width="100%"
            >
              <Text fontSize="20px" fontColor="white">
                예상비용
              </Text>
              <MobileRadioGroup>
                <RadioLabel>
                  <input
                    type="radio"
                    name="budget"
                    value="1000만원 이하"
                    onChange={handleChange}
                    checked={formData.budget === "1000만원 이하"}
                  />
                  1000만원 이하
                </RadioLabel>
                <RadioLabel>
                  <input
                    type="radio"
                    name="budget"
                    value="1500만원~2000만원"
                    onChange={handleChange}
                    checked={formData.budget === "1500만원~2000만원"}
                  />
                  1500~2000만원
                </RadioLabel>
                <RadioLabel>
                  <input
                    type="radio"
                    name="budget"
                    value="2000만원~2500만원"
                    onChange={handleChange}
                    checked={formData.budget === "2000만원~2500만원"}
                  />
                  2000만원~2500만원
                </RadioLabel>
                <RadioLabel>
                  <input
                    type="radio"
                    name="budget"
                    value="2500만원 이상"
                    onChange={handleChange}
                    checked={formData.budget === "2500만원 이상"}
                  />
                  2500만원 이상
                </RadioLabel>
              </MobileRadioGroup>
            </CustomContainer>
          </CustomContainer>
          <CustomContainer
            flexDirection="column"
            width="700px"
            height="150px"
            justifyContent="flex-start"
            alignItems="flex-start"
            gap="10px"
          >
            <Text fontSize="20px" fontColor="white">
              요청사항
            </Text>
            <Textarea
              name="request"
              value={formData.request}
              onChange={handleChange}
            />
          </CustomContainer>
        </CustomContainer>
      </CustomContainer>
      <CustomContainer mobileMargin="20px 0px 0px 0px">
        <Button type="submit" onClick={handleSubmit}>
          견적문의
        </Button>
      </CustomContainer>
    </form>
  );
};

export default MobileHomepageEnquiry;
